import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import './FeedbackPanel.css';

// Absolute base + CORS like the rest of the app. The global fetch
// interceptor attaches the Bearer token to any request hitting this
// origin, so plain fetch() below needs no auth plumbing.
const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

const MAX_FILES = 10;
const MAX_SIZE = 25 * 1024 * 1024; // 25 MB per file — mirrors the server multer limit.

interface FeedbackFile {
  id: string;
  filename: string;
  mime: string | null;
  size_bytes: number | null;
  created_at: string;
}

interface FeedbackEntry {
  id: string;
  author_id: string | null;
  author_name: string;
  title: string;
  body: string;
  created_at: string;
  updated_at: string;
  files: FeedbackFile[];
  // Optional context — set when the feedback was left against a poster
  // or a timeline estimate. Joined names come back with the list.
  project_id: string | null;
  project_name: string | null;
  estimate_id: string | null;
  estimate_title: string | null;
  estimate_status: string | null;
}

type EstimateStatus = 'review' | 'approved' | 'rejected';

interface ContextEstimate {
  id: string;
  title: string;
  status: EstimateStatus;
  model: string | null;
  created_at: string;
}

interface ContextProject {
  id: string;
  name: string;
  updated_at: string;
  has_spec: boolean;
  estimates: ContextEstimate[];
}

interface Toast {
  id: number;
  msg: string;
  kind: 'ok' | 'err';
}

const EST_STATUS: Record<EstimateStatus, { label: string; cls: string }> = {
  review: { label: 'На проверке', cls: 'fb-est-chip-review' },
  approved: { label: 'Принята', cls: 'fb-est-chip-approved' },
  rejected: { label: 'Отклонена', cls: 'fb-est-chip-rejected' },
};

// The estimator runs two model tiers — the CLI Claude ("Lite") and the
// White-label: NEVER echo a raw provider model id to the UI — unknown
// values collapse to a generic 'AI'.
function modelLabel(model: string | null): string | null {
  if (!model) return null;
  if (model.startsWith('claude-opus') || model === 'opus') return 'Lite';
  if (model.startsWith('gpt-')) return 'Max';
  return 'AI';
}

// Server error codes → Russian copy. Falls back to the server's own
// `message`, then a generic line, so nothing surfaces as a raw code.
const ERROR_RU: Record<string, string> = {
  invalid_title: 'Заголовок обязателен (1–200 символов)',
  no_files: 'Не выбрано ни одного файла',
  not_found: 'Запись не найдена',
  forbidden: 'Недостаточно прав',
  invalid_project: 'Архитектура не найдена',
  invalid_estimate: 'Оценка не найдена',
  no_svg: 'У этой архитектуры ещё нет постера',
  no_docx: 'Для этой оценки нет .docx',
};

async function readError(res: Response): Promise<string> {
  const data = await res.json().catch(() => ({} as any));
  // Localized copy for known codes wins over the server's English message.
  return ERROR_RU[data.error as string] || data.message || data.error || `Ошибка (${res.status})`;
}

function fmtSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  // дд.мм, чч:мм
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Context to attach to a new feedback entry. Both ids may be present
//    (feedback on an estimate carries its project too). ─────────────
interface FeedbackContext {
  projectId?: string;
  estimateId?: string;
}

interface FeedbackFormProps {
  context?: FeedbackContext;
  variant?: 'full' | 'compact';
  bodyPlaceholder: string;
  submitLabel: string;
  onCreated: () => void | Promise<void>;
  pushToast: (msg: string, kind: 'ok' | 'err') => void;
}

// Self-contained create form. Owns its own title/body/file state so the
// panel can mount several (freeform, per-project, per-estimate) at once
// without them fighting over one shared draft. Client-side file guards
// and the two-step create→upload flow mirror the original panel.
const FeedbackForm: React.FC<FeedbackFormProps> = ({
  context,
  variant = 'full',
  bodyPlaceholder,
  submitLabel,
  onCreated,
  pushToast,
}) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming);
      setFiles((prev) => {
        const next = [...prev];
        for (const f of list) {
          if (next.length >= MAX_FILES) {
            pushToast(`Максимум ${MAX_FILES} файлов`, 'err');
            break;
          }
          if (f.size > MAX_SIZE) {
            pushToast(`«${f.name}»: больше 25 МБ`, 'err');
            continue;
          }
          if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
          next.push(f);
        }
        return next;
      });
    },
    [pushToast],
  );

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    // Reset so picking the same file again re-fires onChange.
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeStagedFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    const t = title.trim();
    if (!t) {
      pushToast('Введите заголовок', 'err');
      return;
    }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = { title: t, body };
      if (context?.projectId) payload.project_id = context.projectId;
      if (context?.estimateId) payload.estimate_id = context.estimateId;
      const res = await fetch(`${API}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      const entry: FeedbackEntry | undefined = data.entry;
      if (entry && files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append('files', f));
        const fr = await fetch(`${API}/api/feedback/${entry.id}/files`, {
          method: 'POST',
          body: fd,
        });
        if (!fr.ok) {
          // Entry exists but the attachments failed: keep the staged
          // files so nothing is lost, refresh the list, and DON'T claim
          // success.
          pushToast(`Фидбэк создан, но файлы не загрузились: ${await readError(fr)}`, 'err');
          await onCreated();
          return;
        }
      }
      setTitle('');
      setBody('');
      setFiles([]);
      await onCreated();
      pushToast('Фидбэк оставлен', 'ok');
    } catch {
      pushToast('Сетевая ошибка при отправке', 'err');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`fb-form${variant === 'compact' ? ' fb-form-compact' : ''}`}>
      <input
        className="form-input fb-field"
        placeholder="Заголовок"
        value={title}
        maxLength={200}
        disabled={submitting}
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        className="form-textarea fb-field fb-body-input"
        placeholder={bodyPlaceholder}
        value={body}
        disabled={submitting}
        onChange={(e) => setBody(e.target.value)}
      />

      <div
        className={`fb-dropzone${dragActive ? ' fb-dropzone-active' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <span className="fb-drop-plus">+</span>
        <p className="fb-drop-hint">Скриншоты, доки, примеры — что приложить к фидбэку</p>
        <p className="fb-drop-sub">
          Перетащите или выберите — до {MAX_FILES} файлов, ≤ 25 МБ каждый
        </p>
        <input ref={fileInputRef} type="file" multiple hidden onChange={onFileInput} />
      </div>

      {files.length > 0 && (
        <div className="fb-staged">
          {files.map((f, i) => (
            <span className="fb-staged-chip" key={`${f.name}-${f.size}-${i}`}>
              <span className="fb-staged-name">{f.name}</span>
              <span className="fb-staged-size">{fmtSize(f.size)}</span>
              <button
                type="button"
                className="fb-staged-x"
                title="Убрать"
                disabled={submitting}
                onClick={() => removeStagedFile(i)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="fb-create-actions">
        <button className="btn-primary" disabled={submitting || !title.trim()} onClick={submit}>
          {submitting ? 'Отправка…' : submitLabel}
        </button>
      </div>
    </div>
  );
};

const FeedbackPanel: React.FC = () => {
  const { user, hasPermission } = useAuth();

  // All entries (own view). Carries optional poster/estimate context.
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Architectures the manager can browse.
  const [projects, setProjects] = useState<ContextProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Which architecture is open (null = the browse/list view).
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);

  // Poster SVG for the open architecture, fetched on demand.
  const [svgText, setSvgText] = useState<string | null>(null);
  const [svgLoading, setSvgLoading] = useState(false);
  const [svgError, setSvgError] = useState<string | null>(null);

  // Which estimate's inline "feedback on estimate" form is expanded.
  const [openEstimateForm, setOpenEstimateForm] = useState<string | null>(null);

  // Inline edit (shared across every card the panel renders).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);

  const [toasts, setToasts] = useState<Toast[]>([]);

  const canSubmit = hasPermission('feedback:submit');
  const canManage = useCallback(
    (entry: FeedbackEntry) =>
      !!user?.isRoot || (canSubmit && !!user && entry.author_id === user.id),
    [user, canSubmit],
  );

  const pushToast = useCallback((msg: string, kind: 'ok' | 'err') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, msg, kind }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4200);
  }, []);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch(`${API}/api/feedback`);
      if (!res.ok) {
        setLoadError(await readError(res));
        setEntries([]);
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch {
      setLoadError('Не удалось загрузить фидбэк — проверьте соединение.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    setProjectsError(null);
    try {
      const res = await fetch(`${API}/api/feedback/context/projects`);
      if (!res.ok) {
        setProjectsError(await readError(res));
        setProjects([]);
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      setProjects(Array.isArray(data.projects) ? data.projects : []);
    } catch {
      setProjectsError('Не удалось загрузить архитектуры — проверьте соединение.');
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Idempotent fetches — safe under StrictMode's double-invoked effects.
    load();
    loadProjects();
  }, [load, loadProjects]);

  const openProject = projects.find((p) => p.id === openProjectId) || null;

  // Fetch the poster SVG whenever an architecture with a spec is opened.
  useEffect(() => {
    if (!openProject || !openProject.has_spec) {
      setSvgText(null);
      setSvgError(null);
      setSvgLoading(false);
      return;
    }
    let alive = true;
    const controller = new AbortController();
    setSvgText(null);
    setSvgError(null);
    setSvgLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/feedback/context/projects/${openProject.id}/svg`,
          { signal: controller.signal },
        );
        if (!alive) return;
        if (!res.ok) {
          setSvgError(await readError(res));
          return;
        }
        const text = await res.text();
        if (alive) setSvgText(text);
      } catch (err) {
        if (alive && (err as any)?.name !== 'AbortError') {
          setSvgError('Не удалось загрузить постер — проверьте соединение.');
        }
      } finally {
        if (alive) setSvgLoading(false);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [openProject]);

  const enterProject = (id: string) => {
    setOpenProjectId(id);
    setOpenEstimateForm(null);
    cancelEdit();
  };

  const exitProject = () => {
    setOpenProjectId(null);
    setOpenEstimateForm(null);
    cancelEdit();
  };

  // ── Edit ──────────────────────────────────────────────────────────
  const startEdit = (entry: FeedbackEntry) => {
    setEditingId(entry.id);
    setEditTitle(entry.title);
    setEditBody(entry.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditBody('');
  };

  const saveEdit = async (id: string) => {
    const t = editTitle.trim();
    if (!t) {
      pushToast('Введите заголовок', 'err');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/feedback/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: t, body: editBody }),
      });
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      const updated: FeedbackEntry | undefined = data.entry;
      setEntries((prev) =>
        prev.map((e) =>
          e.id === id
            ? updated
              ? { ...updated, files: updated.files ?? e.files }
              : { ...e, title: t, body: editBody }
            : e,
        ),
      );
      cancelEdit();
      pushToast('Изменения сохранены', 'ok');
    } catch {
      pushToast('Сетевая ошибка при сохранении', 'err');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────
  const deleteEntry = async (entry: FeedbackEntry) => {
    if (!window.confirm(`Удалить фидбэк «${entry.title}»? Файлы тоже пропадут.`)) return;
    try {
      const res = await fetch(`${API}/api/feedback/${entry.id}`, { method: 'DELETE' });
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      if (editingId === entry.id) cancelEdit();
    } catch {
      pushToast('Сетевая ошибка при удалении', 'err');
    }
  };

  const deleteFile = async (entryId: string, file: FeedbackFile) => {
    if (!window.confirm(`Удалить файл «${file.filename}»?`)) return;
    try {
      const res = await fetch(`${API}/api/feedback/${entryId}/files/${file.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entryId ? { ...e, files: e.files.filter((f) => f.id !== file.id) } : e,
        ),
      );
    } catch {
      pushToast('Сетевая ошибка при удалении файла', 'err');
    }
  };

  // ── Download (raw fetch → blob → objectURL) ───────────────────────
  const downloadFile = async (entryId: string, file: FeedbackFile) => {
    try {
      const res = await fetch(`${API}/api/feedback/${entryId}/files/${file.id}/download`);
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      await streamToDownload(res, file.filename);
    } catch {
      pushToast('Не удалось скачать файл', 'err');
    }
  };

  const downloadDocx = async (estimate: ContextEstimate) => {
    try {
      const res = await fetch(`${API}/api/feedback/context/estimates/${estimate.id}/docx`);
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      // Prefer the server's Content-Disposition filename; fall back to a
      // slug of the estimate title.
      const cd = res.headers.get('content-disposition') || '';
      const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
      const fallback = `${estimate.title.replace(/[^\wа-яё\- ]+/gi, '').trim() || 'estimate'}.docx`;
      const name = m ? decodeURIComponent(m[1].replace(/"/g, '')) : fallback;
      await streamToDownload(res, name);
    } catch {
      pushToast('Не удалось скачать .docx', 'err');
    }
  };

  async function streamToDownload(res: Response, filename: string) {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // Access guard — the surface is only shown to permitted users, but keep
  // a graceful notice in case the panel is ever mounted without access.
  if (!hasPermission('feedback:view') && !canSubmit) {
    return (
      <div className="feedback-panel">
        <div className="fb-notice">Нет доступа к фидбэку.</div>
      </div>
    );
  }

  // ── One feedback card (view or inline-edit) ───────────────────────
  const renderCard = (entry: FeedbackEntry) => {
    const editing = editingId === entry.id;
    return (
      <article className="fb-card" key={entry.id}>
        {editing ? (
          <div className="fb-edit">
            <input
              className="form-input fb-field"
              value={editTitle}
              maxLength={200}
              disabled={saving}
              onChange={(e) => setEditTitle(e.target.value)}
            />
            <textarea
              className="form-textarea fb-field fb-body-input"
              value={editBody}
              disabled={saving}
              onChange={(e) => setEditBody(e.target.value)}
            />
            <div className="fb-edit-actions">
              <button
                className="btn-primary"
                disabled={saving || !editTitle.trim()}
                onClick={() => saveEdit(entry.id)}
              >
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
              <button className="btn-secondary" disabled={saving} onClick={cancelEdit}>
                Отмена
              </button>
            </div>
          </div>
        ) : (
          <>
            <header className="fb-card-head">
              <h3 className="fb-card-title">{entry.title}</h3>
              {canManage(entry) && (
                <div className="fb-card-controls">
                  <button
                    className="fb-icon-btn"
                    title="Редактировать"
                    onClick={() => startEdit(entry)}
                  >
                    ✎
                  </button>
                  <button
                    className="fb-icon-btn fb-icon-danger"
                    title="Удалить"
                    onClick={() => deleteEntry(entry)}
                  >
                    🗑
                  </button>
                </div>
              )}
            </header>
            <div className="fb-card-meta">
              <span className="fb-author">{entry.author_name}</span>
              <span className="fb-dot">·</span>
              <span className="fb-date">{fmtDate(entry.created_at)}</span>
            </div>
            {(entry.estimate_id || entry.project_id) && (
              <div className="fb-ctx-chips">
                {entry.estimate_id ? (
                  <span className="fb-ctx-chip fb-ctx-chip-est">
                    Оценка: {entry.estimate_title || 'без названия'}
                  </span>
                ) : (
                  <span className="fb-ctx-chip fb-ctx-chip-poster">
                    Постер: {entry.project_name || 'без названия'}
                  </span>
                )}
              </div>
            )}
            {entry.body && <div className="fb-card-body">{entry.body}</div>}
            {entry.files.length > 0 && (
              <div className="fb-files">
                {entry.files.map((file) => (
                  <span className="fb-file-chip" key={file.id}>
                    <button
                      type="button"
                      className="fb-file-dl"
                      title="Скачать"
                      onClick={() => downloadFile(entry.id, file)}
                    >
                      <span className="fb-file-name">{file.filename}</span>
                      <span className="fb-file-size">{fmtSize(file.size_bytes)}</span>
                    </button>
                    {canManage(entry) && (
                      <button
                        type="button"
                        className="fb-file-x"
                        title="Удалить файл"
                        onClick={() => deleteFile(entry.id, file)}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </article>
    );
  };

  // ── Project (architecture) detail view ────────────────────────────
  const renderProjectView = (project: ContextProject) => {
    const projectEntries = entries.filter((e) => e.project_id === project.id);
    return (
      <div className="fb-project">
        <button className="btn-secondary fb-back" onClick={exitProject}>
          ← Все архитектуры
        </button>

        <header className="fb-project-head">
          <h2 className="fb-project-title">{project.name}</h2>
          <div className="fb-project-meta">
            <span className="fb-date">обновлено {fmtDate(project.updated_at)}</span>
            <span className="fb-dot">·</span>
            <span>
              {project.estimates.length}{' '}
              {declOf(project.estimates.length, 'оценка', 'оценки', 'оценок')}
            </span>
          </div>
        </header>

        {/* Poster */}
        <section className="fb-section">
          <h3 className="fb-section-title">Постер</h3>
          {!project.has_spec ? (
            <div className="fb-poster-empty">У этой архитектуры ещё нет постера.</div>
          ) : svgLoading ? (
            <div className="fb-notice">Загрузка постера…</div>
          ) : svgError ? (
            <div className="fb-notice fb-notice-err">{svgError}</div>
          ) : svgText ? (
            <div
              className="fb-poster"
              // Read-only render of the server SVG. No editor handlers are
              // attached — CSS scales it to the container width.
              dangerouslySetInnerHTML={{ __html: svgText }}
            />
          ) : null}
        </section>

        {/* Estimates */}
        <section className="fb-section">
          <h3 className="fb-section-title">Оценки сроков</h3>
          {project.estimates.length === 0 ? (
            <div className="fb-poster-empty">Оценок пока нет.</div>
          ) : (
            <div className="fb-est-list">
              {project.estimates.map((est) => {
                const st = EST_STATUS[est.status];
                const ml = modelLabel(est.model);
                const formOpen = openEstimateForm === est.id;
                return (
                  <div className="fb-est-item" key={est.id}>
                    <div className="fb-est-top">
                      <div className="fb-est-main">
                        <span className="fb-est-title">{est.title || 'Без названия'}</span>
                        <div className="fb-est-meta">
                          {ml && <span className="fb-est-model">{ml}</span>}
                          {st && (
                            <span className={`fb-est-chip ${st.cls}`}>{st.label}</span>
                          )}
                          <span className="fb-dot">·</span>
                          <span className="fb-date">{fmtDate(est.created_at)}</span>
                        </div>
                      </div>
                      <div className="fb-est-actions">
                        <button
                          className="btn-secondary fb-est-btn"
                          onClick={() => downloadDocx(est)}
                        >
                          Скачать .docx
                        </button>
                        {canSubmit && (
                          <button
                            className="btn-secondary fb-est-btn"
                            onClick={() =>
                              setOpenEstimateForm(formOpen ? null : est.id)
                            }
                          >
                            {formOpen ? 'Свернуть' : 'Фидбэк по оценке'}
                          </button>
                        )}
                      </div>
                    </div>
                    {canSubmit && formOpen && (
                      <div className="fb-est-form">
                        <FeedbackForm
                          variant="compact"
                          context={{ projectId: project.id, estimateId: est.id }}
                          bodyPlaceholder={`Что уточнить по оценке «${est.title}»…`}
                          submitLabel="Отправить по оценке"
                          pushToast={pushToast}
                          onCreated={async () => {
                            await load();
                            setOpenEstimateForm(null);
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Feedback on the architecture */}
        {canSubmit && (
          <section className="fb-section">
            <h3 className="fb-section-title">Оставить фидбэк по архитектуре</h3>
            <FeedbackForm
              context={{ projectId: project.id }}
              bodyPlaceholder="Что улучшить в этой архитектуре…"
              submitLabel="Оставить фидбэк"
              pushToast={pushToast}
              onCreated={load}
            />
          </section>
        )}

        {/* Existing feedback for this architecture / its estimates */}
        <section className="fb-section">
          <h3 className="fb-section-title">Фидбэк по этой архитектуре</h3>
          {loading ? (
            <div className="fb-notice">Загрузка…</div>
          ) : projectEntries.length === 0 ? (
            <div className="fb-empty">Фидбэка по этой архитектуре пока нет.</div>
          ) : (
            <div className="fb-list">{projectEntries.map(renderCard)}</div>
          )}
        </section>
      </div>
    );
  };

  // ── Browse view: architectures + freeform feedback ────────────────
  const renderBrowse = () => {
    const freeEntries = entries.filter((e) => !e.project_id && !e.estimate_id);
    return (
      <>
        <section className="fb-section">
          <div className="fb-section-head">
            <h2 className="fb-section-title fb-section-title-lg">Архитектуры</h2>
            <p className="fb-section-lede">
              Откройте архитектуру, чтобы посмотреть постер и оставить фидбэк в контексте.
            </p>
          </div>

          {projectsLoading ? (
            <div className="fb-notice">Загрузка архитектур…</div>
          ) : projectsError ? (
            <div className="fb-notice fb-notice-err">
              {projectsError}
              <button className="btn-secondary fb-retry" onClick={loadProjects}>
                Повторить
              </button>
            </div>
          ) : projects.length === 0 ? (
            <div className="fb-empty">Архитектур пока нет.</div>
          ) : (
            <div className="fb-proj-grid">
              {projects.map((p) => (
                <button
                  className="fb-proj-card"
                  key={p.id}
                  onClick={() => enterProject(p.id)}
                >
                  <span className="fb-proj-name">{p.name}</span>
                  <span className="fb-proj-sub">
                    <span
                      className={`fb-proj-flag${p.has_spec ? ' fb-proj-flag-ok' : ''}`}
                    >
                      {p.has_spec ? 'постер готов' : 'пусто'}
                    </span>
                    <span className="fb-dot">·</span>
                    <span>
                      {p.estimates.length}{' '}
                      {declOf(p.estimates.length, 'оценка', 'оценки', 'оценок')}
                    </span>
                  </span>
                  <span className="fb-proj-date">обновлено {fmtDate(p.updated_at)}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="fb-section">
          <div className="fb-section-head">
            <h2 className="fb-section-title fb-section-title-lg">Общий фидбэк</h2>
            <p className="fb-section-lede">
              Заметки без привязки к конкретной архитектуре или оценке.
            </p>
          </div>

          {canSubmit && (
            <FeedbackForm
              bodyPlaceholder="Текст / заметки — что улучшить в будущих архитектурах…"
              submitLabel="Оставить фидбэк"
              pushToast={pushToast}
              onCreated={load}
            />
          )}

          {loading ? (
            <div className="fb-notice">Загрузка…</div>
          ) : loadError ? (
            <div className="fb-notice fb-notice-err">
              {loadError}
              <button className="btn-secondary fb-retry" onClick={load}>
                Повторить
              </button>
            </div>
          ) : freeEntries.length === 0 ? (
            <div className="fb-empty">Общего фидбэка пока нет.</div>
          ) : (
            <div className="fb-list">{freeEntries.map(renderCard)}</div>
          )}
        </section>
      </>
    );
  };

  return (
    <div className="feedback-panel">
      {openProject ? renderProjectView(openProject) : renderBrowse()}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fb-toasts">
          {toasts.map((t) => (
            <div key={t.id} className={`fb-toast fb-toast-${t.kind}`}>
              {t.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Russian plural picker (1 оценка / 2 оценки / 5 оценок).
function declOf(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

export default FeedbackPanel;
